import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles"
import AttachMoneyIcon from "@material-ui/icons/AttachMoney"
import BarChartIcon from "@material-ui/icons/BarChart"
import GroupIcon from "@material-ui/icons/Group"
import LocalMallIcon from "@material-ui/icons/LocalMall"
import PaymentIcon from "@material-ui/icons/Payment"
import ScheduleIcon from "@material-ui/icons/Schedule"
import SyncAltIcon from "@material-ui/icons/SyncAlt"
import VisibilityIcon from "@material-ui/icons/Visibility"
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import useAppStore from "../../../stores/AppStore"
import { ResumoFornecedorActionsTypes } from "../context/resumoFornecedorActions"
import { useResumoFornecedorContext } from "../context/useResumoFornecedorContext"
import type { PontoDia, PontoMes } from "../models/resumoFornecedorModel"

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
const num = new Intl.NumberFormat("pt-BR")

// Preferência do "olho" (ocultar valores financeiros), salva por dispositivo.
const CHAVE_OCULTAR_VALORES = "ocultarValoresResumo"
const VALOR_OCULTO = "R$ •••••"

function lerOcultarValores(): boolean {
    try {
        return localStorage.getItem(CHAVE_OCULTAR_VALORES) === "1"
    } catch {
        return false
    }
}

function salvarOcultarValores(ocultar: boolean) {
    try {
        localStorage.setItem(CHAVE_OCULTAR_VALORES, ocultar ? "1" : "0")
    } catch {
        // sem storage (modo privado etc.): a escolha vale só para esta sessão
    }
}

function formatarFrequencia(v: number): string {
    return v.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) + " x/mês"
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    kpiCard: {
        borderRadius: 16,
        padding: theme.spacing(2.5),
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        height: "100%",
    },
    kpiIcon: {
        width: 48,
        height: 48,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    kpiLabel: {
        fontSize: "0.7rem",
        fontWeight: 600,
        textTransform: "uppercase",
        color: "#7f8c8d",
        letterSpacing: "0.05em",
    },
    kpiValue: {
        fontWeight: 700,
        color: "#2c3e50",
        fontSize: "1.3rem",
        lineHeight: 1.2,
    },
    progressItem: {
        marginBottom: theme.spacing(2),
    },
    progressLabel: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 4,
    },
    barChartContainer: {
        display: "flex",
        alignItems: "flex-end",
        gap: 4,
        height: 140,
        overflowX: "auto",
        paddingBottom: theme.spacing(1),
    },
    barItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 32,
        flex: 1,
    },
    barFill: {
        width: "100%",
        borderRadius: "4px 4px 0 0",
        minHeight: 2,
        transition: "height 0.3s",
    },
    barLabel: {
        fontSize: "0.6rem",
        color: "#7f8c8d",
        marginTop: 2,
        textAlign: "center",
        whiteSpace: "nowrap",
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    btnAtualizar: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    saudacaoLinha: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    btnOlho: {
        color: "#7f8c8d",
    },
    emptyMsg: {
        color: "#7f8c8d",
        fontStyle: "italic",
        textAlign: "center",
        padding: theme.spacing(2),
    },
}))

const TooltipGrafico = withStyles({
    tooltip: {
        backgroundColor: "#2c3e50",
        color: "white",
        fontSize: "0.9rem",
        padding: "10px 14px",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        maxWidth: 280,
    },
    arrow: { color: "#2c3e50" },
})(Tooltip)

function pad2(n: number): string {
    return String(n).padStart(2, "0")
}

// "Terça, 22/09" (o label do backend já vem com o dia da semana).
function tituloDia(p: PontoDia): string {
    const data = `${pad2(p.dia)}/${pad2(p.mes)}`
    return p.label ? `${p.label}, ${data}` : data
}

// Dia equivalente no período de comparação (o backend compara com o período anterior
// de mesmo tamanho: na tela inicial, 7 dias = mesmo dia da semana anterior).
function diaComparacao(p: PontoDia, deslocamentoDias: number): string {
    const d = new Date(p.ano, p.mes - 1, p.dia - deslocamentoDias)
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`
}

function Variacao({
    atual,
    anterior,
    formatar,
    referencia,
}: {
    atual: number
    anterior: number
    formatar: (v: number) => string
    referencia: string
}) {
    if (anterior === 0 && atual === 0) {
        return <Box style={{ color: "#bdc3c7", marginTop: 6 }}>Sem movimento {referencia} também</Box>
    }
    if (anterior === 0) {
        return (
            <Box style={{ color: "#2ecc71", marginTop: 6 }}>
                ▲ Subiu — sem movimento {referencia}
            </Box>
        )
    }
    const pct = ((atual - anterior) / anterior) * 100
    const subiu = pct > 0
    const igual = Math.abs(pct) < 0.05
    const cor = igual ? "#bdc3c7" : subiu ? "#2ecc71" : "#ff7675"
    const seta = igual ? "=" : subiu ? "▲" : "▼"
    const texto = igual ? "Igual" : subiu ? "Subiu" : "Caiu"
    const pctFmt = Math.abs(pct).toLocaleString("pt-BR", { maximumFractionDigits: 1 })
    return (
        <Box style={{ marginTop: 6 }}>
            <Box style={{ color: cor, fontWeight: 700 }}>
                {seta} {texto}
                {!igual && ` ${pctFmt}%`}
            </Box>
            <Box style={{ color: "#bdc3c7", fontSize: "0.8rem" }}>
                {referencia}: {formatar(anterior)}
            </Box>
        </Box>
    )
}

function BarChart({
    dados,
    getValor,
    getLabel,
    cor,
    renderTooltip,
}: {
    dados: (PontoDia | PontoMes)[]
    getValor: (p: PontoDia | PontoMes) => number
    getLabel: (p: PontoDia | PontoMes) => string
    cor: string
    renderTooltip: (p: PontoDia | PontoMes) => React.ReactNode
}) {
    const classes = useStyles()
    const max = Math.max(...dados.map(getValor), 1)
    if (dados.length === 0) return <Typography className={classes.emptyMsg}>Não há registros nesse período.</Typography>
    return (
        <Box className={classes.barChartContainer}>
            {dados.map((p, i) => {
                const val = getValor(p)
                const px = Math.max((val / max) * 110, 2)
                return (
                    <TooltipGrafico key={i} title={<>{renderTooltip(p)}</>} arrow placement="top">
                        <Box className={classes.barItem}>
                            <Box
                                className={classes.barFill}
                                style={{ height: `${px}px`, backgroundColor: cor }}
                            />
                            <Typography className={classes.barLabel}>{getLabel(p)}</Typography>
                        </Box>
                    </TooltipGrafico>
                )
            })}
        </Box>
    )
}

function KpiCard({
    label,
    value,
    icon,
    iconBg,
}: {
    label: string
    value: string
    icon: React.ReactNode
    iconBg: string
}) {
    const classes = useStyles()
    return (
        <Box className={classes.kpiCard}>
            <Box className={classes.kpiIcon} style={{ backgroundColor: iconBg }}>
                {icon}
            </Box>
            <Box>
                <Typography className={classes.kpiLabel}>{label}</Typography>
                <Typography className={classes.kpiValue}>{value}</Typography>
            </Box>
        </Box>
    )
}

function SecaoCard({ titulo, icon, children }: { titulo: string; icon: React.ReactNode; children: React.ReactNode }) {
    const classes = useStyles()
    return (
        <Paper className={classes.configCard}>
            <Box className={classes.cardHeader}>
                <Box className={classes.iconHeader}>{icon}</Box>
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    {titulo}
                </Typography>
            </Box>
            <Box className={classes.cardContent}>{children}</Box>
        </Paper>
    )
}

export function DashboardResumo({ modoInicial = false }: { modoInicial?: boolean }) {
    const classes = useStyles()
    const theme = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, erroCarregamento, carregando, buscar, buscarInicial, carregouInicial } =
        useResumoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const configuracoes = useAppStore(s => s.configuracoes)
    const [ocultarValores, setOcultarValores] = useState(lerOcultarValores)
    const usuario = configuracoes?.user?.usuario
    // CLIENTEFIEL na tela inicial: só mostra a saudação + botão "Ver dados" até clicar.
    const aguardandoDados =
        modoInicial && configuracoes?.user?.perfil === "CLIENTEFIEL" && !carregouInicial

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento, enqueueSnackbar])

    const { kpis, possuiDelivery } = dados

    function alternarOcultarValores() {
        const novo = !ocultarValores
        setOcultarValores(novo)
        salvarOcultarValores(novo)
    }

    const moeda = (v: number) => (ocultarValores ? VALOR_OCULTO : brl.format(v))
    // O label do cliente pode vir já formatado em R$ pelo backend.
    const mascararMonetario = (texto: string) =>
        ocultarValores && /R\$/.test(texto) ? VALOR_OCULTO : texto

    // O backend manda em decimal2/valor2 o mesmo dia no período anterior de igual tamanho
    // (tela inicial: 7 dias → mesmo dia da semana anterior).
    const diasPeriodo = dados.pontosFaturamento.length
    const referenciaComparacao = (p: PontoDia) =>
        diasPeriodo === 7
            ? `Semana anterior (${diaComparacao(p, diasPeriodo)})`
            : `Período anterior (${diaComparacao(p, diasPeriodo)})`
    const qtdPedidos = (v: number) => `${num.format(v)} ${v === 1 ? "pedido" : "pedidos"}`

    const tooltipVendas = (ponto: PontoDia | PontoMes) => {
        const p = ponto as PontoDia
        return (
            <>
                <Box style={{ fontWeight: 600, marginBottom: 2 }}>{tituloDia(p)}</Box>
                <Box style={{ fontSize: "1.15rem", fontWeight: 700 }}>{moeda(p.decimal)}</Box>
                <Box style={{ color: "#bdc3c7", fontSize: "0.8rem" }}>{qtdPedidos(p.valor)}</Box>
                <Variacao
                    atual={p.decimal}
                    anterior={p.decimal2}
                    formatar={moeda}
                    referencia={referenciaComparacao(p)}
                />
            </>
        )
    }

    const tooltipPedidos = (ponto: PontoDia | PontoMes) => {
        const p = ponto as PontoDia
        return (
            <>
                <Box style={{ fontWeight: 600, marginBottom: 2 }}>{tituloDia(p)}</Box>
                <Box style={{ fontSize: "1.15rem", fontWeight: 700 }}>{qtdPedidos(p.valor)}</Box>
                <Variacao
                    atual={p.valor}
                    anterior={p.valor2}
                    formatar={qtdPedidos}
                    referencia={referenciaComparacao(p)}
                />
            </>
        )
    }

    const tooltipMes = (ponto: PontoDia | PontoMes) => {
        const m = ponto as PontoMes
        return (
            <>
                <Box style={{ fontWeight: 600, marginBottom: 2 }}>
                    {pad2(m.mes)}/{m.ano}
                </Box>
                <Box style={{ fontSize: "1.15rem", fontWeight: 700 }}>{moeda(m.decimal)}</Box>
                <Box style={{ color: "#bdc3c7", fontSize: "0.8rem" }}>{qtdPedidos(m.valor2)}</Box>
            </>
        )
    }

    const tooltipExpirados = (ponto: PontoDia | PontoMes) => {
        const p = ponto as PontoDia
        return (
            <>
                <Box style={{ fontWeight: 600, marginBottom: 2 }}>{tituloDia(p)}</Box>
                <Box style={{ fontSize: "1.15rem", fontWeight: 700 }}>{moeda(p.decimal)}</Box>
                <Box style={{ color: "#bdc3c7", fontSize: "0.8rem" }}>{qtdPedidos(p.valor)}</Box>
            </>
        )
    }

    const botaoOlho = (
        <Tooltip title={ocultarValores ? "Mostrar valores" : "Ocultar valores"}>
            <IconButton
                className={classes.btnOlho}
                onClick={alternarOcultarValores}
                aria-label={ocultarValores ? "Mostrar valores" : "Ocultar valores"}
            >
                {ocultarValores ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
        </Tooltip>
    )

    function handleBuscar() {
        if (!dados.dataInicio || !dados.dataFim) {
            enqueueSnackbar("Preencha as datas de início e fim.", { variant: "error" })
            return
        }
        if (dados.dataInicio > dados.dataFim) {
            enqueueSnackbar("Data de início não pode ser maior que a data fim.", { variant: "error" })
            return
        }
        buscar()
    }

    if (aguardandoDados) {
        return (
            <Box className={classes.container}>
                <Box className={classes.content}>
                    <Box mb={3}>
                        <Typography variant="h5" style={{ fontWeight: 700, color: "#2c3e50" }}>
                            Olá, {usuario || "seja bem-vindo"}!
                        </Typography>
                        <Typography variant="body1" style={{ color: "#7f8c8d", marginTop: 4 }}>
                            Selecione a loja desejada e clique em "Ver dados" para carregar as
                            vendas dos últimos 7 dias.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        className={classes.btnAtualizar}
                        onClick={() => buscarInicial()}
                        disabled={carregando}
                        startIcon={
                            carregando ? <CircularProgress size={16} color="inherit" /> : undefined
                        }
                    >
                        {carregando ? "Carregando..." : "Ver dados"}
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>

                {modoInicial ? (
                    /* Saudação (tela inicial) */
                    <Box mb={3} className={classes.saudacaoLinha}>
                        <Box>
                            <Typography variant="h5" style={{ fontWeight: 700, color: "#2c3e50" }}>
                                Olá, {usuario || "seja bem-vindo"}!
                            </Typography>
                            <Typography variant="body1" style={{ color: "#7f8c8d", marginTop: 4 }}>
                                Confira as vendas e os pedidos dos últimos 7 dias
                            </Typography>
                        </Box>
                        {botaoOlho}
                    </Box>
                ) : (
                    /* Filtro de datas */
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardContent}>
                            <Box className={classes.filterRow}>
                                <TextField
                                    label="Data Início"
                                    type="date"
                                    size="small"
                                    variant="outlined"
                                    value={dados.dataInicio}
                                    onChange={e =>
                                        dispatch({
                                            type: ResumoFornecedorActionsTypes.ATUALIZAR_DATAS,
                                            payload: { dataInicio: e.target.value, dataFim: dados.dataFim },
                                        })
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="Data Fim"
                                    type="date"
                                    size="small"
                                    variant="outlined"
                                    value={dados.dataFim}
                                    onChange={e =>
                                        dispatch({
                                            type: ResumoFornecedorActionsTypes.ATUALIZAR_DATAS,
                                            payload: { dataInicio: dados.dataInicio, dataFim: e.target.value },
                                        })
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Button
                                    variant="contained"
                                    className={classes.btnAtualizar}
                                    onClick={handleBuscar}
                                    disabled={carregando}
                                    startIcon={carregando ? <CircularProgress size={16} color="inherit" /> : undefined}
                                >
                                    {carregando ? "Carregando..." : "Atualizar"}
                                </Button>
                                <Box flexGrow={1} />
                                {botaoOlho}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* KPIs */}
                <Grid container spacing={2} style={{ marginBottom: 24 }}>
                    <Grid item xs={6} sm={4} md={3}>
                        <KpiCard
                            label="Vendas Pix"
                            value={num.format(kpis.qtdPix)}
                            icon={<PaymentIcon style={{ color: "#e74c3c" }} />}
                            iconBg="#fdecea"
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                        <KpiCard
                            label="Movimentação Pix"
                            value={moeda(kpis.valorPix)}
                            icon={<AttachMoneyIcon style={{ color: "#8e44ad" }} />}
                            iconBg="#f3e5f5"
                        />
                    </Grid>
                    {possuiDelivery && (
                        <Grid item xs={6} sm={4} md={3}>
                            <KpiCard
                                label="Pedidos"
                                value={num.format(kpis.totalPedidos)}
                                icon={<LocalMallIcon style={{ color: "#27ae60" }} />}
                                iconBg="#e8f5e9"
                            />
                        </Grid>
                    )}
                    {possuiDelivery && (
                        <Grid item xs={6} sm={4} md={3}>
                            <KpiCard
                                label="Faturamento"
                                value={moeda(kpis.totalVendido)}
                                icon={<AttachMoneyIcon style={{ color: "#2980b9" }} />}
                                iconBg="#e3f2fd"
                            />
                        </Grid>
                    )}
                    <Grid item xs={6} sm={4} md={3}>
                        <KpiCard
                            label="Clientes"
                            value={num.format(kpis.totalClientes)}
                            icon={<GroupIcon style={{ color: "#f39c12" }} />}
                            iconBg="#fff8e1"
                        />
                    </Grid>
                    {possuiDelivery && (
                        <Grid item xs={6} sm={4} md={3}>
                            <KpiCard
                                label="Ticket Médio"
                                value={moeda(kpis.ticketMedio)}
                                icon={<BarChartIcon style={{ color: "#f39c12" }} />}
                                iconBg="#fff3e0"
                            />
                        </Grid>
                    )}
                    {possuiDelivery && (
                        <Grid item xs={6} sm={4} md={3}>
                            <KpiCard
                                label="Frequência Média"
                                value={formatarFrequencia(kpis.frequenciaMedia)}
                                icon={<SyncAltIcon style={{ color: "#e74c3c" }} />}
                                iconBg="#fce4ec"
                            />
                        </Grid>
                    )}
                    {possuiDelivery && (
                        <Grid item xs={6} sm={4} md={3}>
                            <KpiCard
                                label="Clientes Ativos"
                                value={num.format(kpis.clientesAtivos)}
                                icon={<GroupIcon style={{ color: "#27ae60" }} />}
                                iconBg="#e8f5e9"
                            />
                        </Grid>
                    )}
                </Grid>

                <Grid container spacing={3}>
                    {/* Faturamento no Delivery */}
                    {possuiDelivery && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Vendas" icon={<AttachMoneyIcon />}>
                                <BarChart
                                    dados={dados.pontosFaturamento}
                                    getValor={p => (p as PontoDia).decimal}
                                    getLabel={p => {
                                        const d = p as PontoDia
                                        return d.label || `${d.dia}/${d.mes}`
                                    }}
                                    cor="#4caf50"
                                    renderTooltip={tooltipVendas}
                                />
                            </SecaoCard>
                        </Grid>
                    )}

                    {/* Pedidos por dia */}
                    <Grid item xs={12} md={6}>
                        <SecaoCard titulo="Pedidos" icon={<LocalMallIcon />}>
                            <BarChart
                                dados={dados.pontosFaturamento}
                                getValor={p => (p as PontoDia).valor}
                                getLabel={p => {
                                    const d = p as PontoDia
                                    return d.label || `${d.dia}/${d.mes}`
                                }}
                                cor="#2196f3"
                                renderTooltip={tooltipPedidos}
                            />
                        </SecaoCard>
                    </Grid>

                    {/* Produtos mais vendidos */}
                    {possuiDelivery && dados.pontosProdutoMaisVendido.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Produtos mais vendidos" icon={<BarChartIcon />}>
                                {dados.pontosProdutoMaisVendido.map((p, i) => (
                                    <Box key={i} className={classes.progressItem}>
                                        <Box className={classes.progressLabel}>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {p.nome}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                                {p.valor}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(p.decimal, 100)}
                                            style={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                ))}
                            </SecaoCard>
                        </Grid>
                    )}

                    {/* Melhores clientes */}
                    {possuiDelivery && dados.melhoresClientes.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Melhores clientes" icon={<GroupIcon />}>
                                {dados.melhoresClientes.map((p, i) => (
                                    <Box key={i} className={classes.progressItem}>
                                        <Box className={classes.progressLabel}>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {p.nome}
                                            </Typography>
                                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                                {mascararMonetario(p.label)}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(p.decimal2, 100)}
                                            style={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                ))}
                            </SecaoCard>
                        </Grid>
                    )}

                    {/* Faturamento mensal */}
                    {possuiDelivery && dados.pontosMes.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Faturamento Mensal" icon={<AttachMoneyIcon />}>
                                <BarChart
                                    dados={dados.pontosMes}
                                    getValor={p => (p as PontoMes).decimal}
                                    getLabel={p => {
                                        const m = p as PontoMes
                                        return `${m.mes}/${m.ano}`
                                    }}
                                    cor="#9c27b0"
                                    renderTooltip={tooltipMes}
                                />
                            </SecaoCard>
                        </Grid>
                    )}

                    {/* Informações extras */}
                    {possuiDelivery && dados.pontosInfoExtras.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Informações Extras" icon={<ScheduleIcon />}>
                                {dados.pontosInfoExtras.map((p, i) => {
                                    const max = Math.max(...dados.pontosInfoExtras.map(x => x.valor2), 1)
                                    const pct = (p.valor2 / max) * 100
                                    return (
                                        <Box key={i} className={classes.progressItem}>
                                            <Box className={classes.progressLabel}>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {p.nome}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                                    {p.valor2}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(pct, 100)}
                                                style={{ height: 8, borderRadius: 4 }}
                                            />
                                        </Box>
                                    )
                                })}
                            </SecaoCard>
                        </Grid>
                    )}

                    {/* Pedidos expirados (fora da tela inicial) */}
                    {!modoInicial && possuiDelivery && dados.totalExpirados > 0 && (
                        <Grid item xs={12} md={6}>
                            <SecaoCard titulo="Pedidos Cancelados ou Expirados" icon={<ScheduleIcon />}>
                                <BarChart
                                    dados={dados.pontosExpirado}
                                    getValor={p => (p as PontoDia).decimal}
                                    getLabel={p => {
                                        const d = p as PontoDia
                                        return d.label || `${d.dia}/${d.mes}`
                                    }}
                                    cor="#e74c3c"
                                    renderTooltip={tooltipExpirados}
                                />
                            </SecaoCard>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    )
}
